/* eslint-disable max-len */
import { ColDef, ICellRendererParams, IFilterParams, ValueFormatterParams } from 'ag-grid-community';

import dayjs from 'dayjs';

import { BILLING_TYPES, CONTRACT_STATES_COLORS } from '@/config/statuses.config';
import { NumberUtils } from '@/utils/numbers.utils';
import { canCalculateBillingValues, isEditableContractLine } from '../../helper';
import { ContractLineFilterOptions }
  from '@/components/common/TableGrid/CustomComponents/AgGridSelectableFilter/filters/ContractLineFilterOptions';
import { ContractLineFilterComparators }
  from '@/components/common/TableGrid/CustomComponents/AgGridSelectableFilter/filters/ContractLineFilterComparators';
import { UpliftData_contractLine } from '../../hooks/useConvertUpliftDataFormat';
import { CONTRACT_NUMBER_CHAR_LENGTH_TO_SHOW, DEFAULT_DATE_PATTERN } from '@/config/common.config';
import { FormattingUtils } from '@/utils/Formatting.utils';
import { Pill } from '@/components/common/Pill/Pill';
import { AgGridSelectableFilterOwnProps } from '@/components/common/TableGrid/CustomComponents/AgGridSelectableFilter/AgGridSelectableFilter';

export type ProductData = {
  stockCode: string;
  stockName: string;
};

type UPLIFT_METHOD = {
  formattedType: string
};

export function useColumns(
  calculateContractLineUplift: (
    contractLineID: string, amount: number,
    upliftMethod: string, currentUnitPrice: number,
    currentAnnualBillinPeriodValue: string, currentBillingPeriodValue: string,
    nextUpliftDate: Date) => void,
  onColumnChange: () => void,
  stockCodeAndNames: ProductData[]
): ColDef[] {
  return [
    {
      colId: 'status',
      headerName: 'Status',
      field: 'status',
      autoHeight: true,
      initialWidth: 150,
      lockPosition: true,
      resizable: false,
      suppressSizeToFit: true,
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
      },
      cellRenderer: ({ value }: ICellRendererParams<ContractLine, ContractLine['status']>) => (
        <Pill size="large" background={value?.status && CONTRACT_STATES_COLORS[value.status]}>
          {value?.formattedStatus}
        </Pill>
      ),
      filter: 'SelectableFilter',
      filterParams: ({ api }: IFilterParams): AgGridSelectableFilterOwnProps<ContractLine> => ({
        optionsOrGetterFunc: ContractLineFilterOptions.getStatusFilterOptions(api),
        comparator: ContractLineFilterComparators.statusComparator
      }),
    },
    {
      colId: 'contractNumber',
      headerName: 'Contract Number',
      field: 'contractNumber',
      initialWidth: 190,
      minWidth: 180,
      editable: false,
      resizable: false,
      cellRenderer: (({ value }: ICellRendererParams<ContractLine>) => {
        const titlePopUp = value.length > CONTRACT_NUMBER_CHAR_LENGTH_TO_SHOW && value;
        return (
          <div title={titlePopUp}>
            {FormattingUtils.getValueTrimmed(value, CONTRACT_NUMBER_CHAR_LENGTH_TO_SHOW)}
          </div>
        );
      })
    },
    {
      colId: 'stockCode',
      headerName: 'Stock Code',
      field: 'productCode',
      initialWidth: 130,
      minWidth: 130,
      cellStyle: { textAlign: 'left' },
      editable: false,
      cellRenderer: 'CellRenderer',
      valueGetter: ({ data }) => {
        return (data as ContractLine)?.product?.stockCode;
      },
      filter: 'SelectableFilter',
      filterParams: {
        optionsOrGetterFunc: stockCodeAndNames.length && ContractLineFilterOptions.getStockCodeOptions(stockCodeAndNames),
        comparator: ContractLineFilterComparators.stockCodeComparator,
      } as AgGridSelectableFilterOwnProps<ContractLine>,
    },
    {
      colId: 'stockName',
      headerName: 'Stock Name',
      field: 'productName',
      initialWidth: 170,
      minWidth: 160,
      cellStyle: { textAlign: 'left' },
      editable: false,
      cellRenderer: 'CellRenderer',
      valueGetter: ({ data }) => {
        return (data as ContractLine)?.product?.stockName;
      },
      filter: 'SelectableFilter',
      filterParams: {
        optionsOrGetterFunc: stockCodeAndNames.length && ContractLineFilterOptions.getStockNameOptions(stockCodeAndNames),
        comparator: ContractLineFilterComparators.stockNameComparator
      } as AgGridSelectableFilterOwnProps<ContractLine>,
    },
    {
      colId: 'quantity',
      headerName: 'Quantity',
      field: 'quantity',
      initialWidth: 110,
      minWidth: 110,
      cellStyle: { textAlign: 'right' },
      editable: false,
      cellRenderer: 'CellRenderer',
      valueFormatter: ({ value }) => {
        return NumberUtils.prettifyWithRound(value, 3, ',', 0) ?? '';
      }
    },
    {
      colId: 'pricePerUnit',
      headerName: 'Unit Price',
      field: 'pricePerUnit',
      initialWidth: 150,
      minWidth: 150,
      cellStyle: () => ({ textAlign: 'right' }),
      editable: false,
      valueFormatter: ({ value }) => {
        return NumberUtils.prettifyWithRound(value) ?? '';
      },
    },
    {
      field: 'unitPriceFrequency.formattedFrequency',
      colId: 'unitPriceFrequency',
      headerName: 'Unit Price Frequency',
      initialWidth: 170,
      minWidth: 170,
      cellRenderer: 'CellRenderer',
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
      },
    },
    {
      colId: 'billingInterval',
      headerName: 'Billing Interval',
      field: 'billingInterval.formattedBillingInterval',
      initialWidth: 155,
      minWidth: 155,
      editable: false,
      cellStyle: {
        display: 'flex',
        justifyContent: 'center',
      },
      filter: 'SelectableFilter',
      filterParams: ({ api }: IFilterParams): AgGridSelectableFilterOwnProps<ContractLine> => ({
        optionsOrGetterFunc: ContractLineFilterOptions.getBillingIntervalsOptions(api),
        comparator: ContractLineFilterComparators.billingIntervalComparator
      }),
    },
    {
      colId: 'billingPeriodValue',
      headerName: 'Billing Period Value',
      field: 'billingPeriodValue',
      initialWidth: 170,
      minWidth: 170,
      cellStyle: () => ({ textAlign: 'right' }),
      valueFormatter: ({ value }) => {
        return NumberUtils.prettifyWithRound(value) ?? '';
      }
    },
    {
      colId: 'annualBillingPeriodValue',
      headerName: 'Annual Billing Value',
      field: 'annualBillingPeriodValue',
      initialWidth: 220,
      minWidth: 220,
      cellStyle: () => ({ textAlign: 'right' }),
      valueFormatter: ({ value }) => {
        return NumberUtils.prettifyWithRound(value) ?? '';
      }
    },
    {
      colId: 'contractStartDate',
      headerName: 'Contract Start Date',
      field: 'contractStartDate',
      initialWidth: 220,
      minWidth: 220,
      valueFormatter: ({ value }: ValueFormatterParams<ContractLine>) => {
        if (value && !Number.isNaN(Date.parse(value))) {
          return `${dayjs(value).format(DEFAULT_DATE_PATTERN)}`;
        }
        return '';
      }
    },
    {
      colId: 'renewableTerm',
      headerName: 'Renewable Term',
      field: 'renewableTerm',
      initialWidth: 220,
      minWidth: 220,
      cellStyle: () => ({ textAlign: 'right' }),
    },
    {
      colId: 'daysBeforeRenewal',
      headerName: 'Days Before Renewal',
      field: 'daysBeforeRenewal',
      initialWidth: 220,
      minWidth: 220,
      cellStyle: () => ({ textAlign: 'right' }),
    },
    {
      colId: 'pONumber',
      headerName: 'PO Number',
      field: 'pONumber',
      initialWidth: 220,
      minWidth: 220,
    },
    {
      colId: 'poEndDate',
      headerName: 'PO End Date',
      field: 'poEndDate',
      initialWidth: 220,
      minWidth: 220,
      valueFormatter: ({ value }: ValueFormatterParams<ContractLine>) => {
        if (value && !Number.isNaN(Date.parse(value))) {
          return `${dayjs(value).format(DEFAULT_DATE_PATTERN)}`;
        }
        return '';
      }
    },
    {
      colId: 'poRemainingValue',
      field: 'poRemainingValue',
      headerName: 'PO Remaining Value',
      initialWidth: 260,
      minWidth: 260,
      cellStyle: () => ({ textAlign: 'right' }),
      valueFormatter: ({ value }) => {
        return NumberUtils.prettifyWithRound(value) ?? '';
      }
    },
    {
      colId: 'nextUpliftDate',
      headerName: 'Next Uplift Date',
      field: 'nextUpliftDate',
      editable: true,
      initialWidth: 220,
      minWidth: 220,
      cellEditor: 'DatepickerEditor',
      cellRenderer: 'CellRenderer',
      cellEditorParams: () => ({
        keepFocusOnCalendarClosed: true,
        saveOnChange: true,
      }),
      valueFormatter: ({ value }: ValueFormatterParams<ContractLine>) => {
        if (value && !Number.isNaN(Date.parse(value))) {
          return `${dayjs(value).format(DEFAULT_DATE_PATTERN)}`;
        }
        return '';
      },
      valueSetter: (params) => {
        params.data.nextUpliftDate = params.newValue ? params.newValue : null;
        if (canCalculateBillingValues(params)) {
          calculateContractLineUplift(params.data.contractLineID, NumberUtils.parseNumericInput(String(params.data.upliftValue)),
            params.data.billingType, params.data.pricePerUnit,
            params.data.annualBillingPeriodValue, params.data.billingPeriodValue,
            params.data.nextUpliftDate);
          onColumnChange();
        }
        return true;
      },
    },
    {
      colId: 'upliftValue',
      headerName: 'Uplift Value',
      field: 'upliftValue',
      initialWidth: 150,
      minWidth: 150,
      cellStyle: () => ({ textAlign: 'right' }),
      editable: isEditableContractLine,
      cellEditor: 'agTextCellEditor',
      cellEditorParams: {
        useFormatter: true
      },
      cellRenderer: ({ value }: ICellRendererParams<UpliftData_contractLine, UpliftData_contractLine['upliftValue']>) => {
        return (value || value === 0) ? NumberUtils.prettifyWithRound(value) : '';
      },
      valueSetter: (params) => {
        if (params.newValue && !NumberUtils.isNumeric(String(params.newValue).replace(/,/g, ''))) {
          return false;
        }
        if (!params.newValue) {
          params.data.newBillingPeriodValue = '';
          params.data.newAnnualBillingPeriodValue = '';
        }
        params.data.upliftValue = params.newValue;

        if (canCalculateBillingValues(params)) {
          calculateContractLineUplift(params.data.contractLineID, NumberUtils.parseNumericInput(String(params.data.upliftValue)),
            params.data.billingType, params.data.pricePerUnit,
            params.data.annualBillingPeriodValue, params.data.billingPeriodValue,
            params.data.nextUpliftDate);
          if (canCalculateBillingValues(params)) onColumnChange();
        }
        return true;
      }
    },
    {
      colId: 'billingType',
      headerName: 'Uplift Method',
      field: 'billingType',
      initialWidth: 150,
      minWidth: 150,
      editable: isEditableContractLine,
      cellEditor: 'DropdownEditor',
      cellEditorParams: () => ({
        options: BILLING_TYPES,
        getOptionValue: (option: UPLIFT_METHOD) => option.formattedType,
        getOptionLabel: (option: UPLIFT_METHOD) => option.formattedType,
        saveOnChange: true
      }),
      cellRenderer: 'CellRenderer',
      valueSetter: (params) => {
        params.data.billingType = params.newValue.formattedType;

        if (canCalculateBillingValues(params)) {
          calculateContractLineUplift(params.data.contractLineID, NumberUtils.parseNumericInput(String(params.data.upliftValue)),
            params.data.billingType, params.data.pricePerUnit,
            params.data.annualBillingPeriodValue, params.data.billingPeriodValue,
            params.data.nextUpliftDate);
          onColumnChange();
        }

        return true;
      }
    },
    {
      colId: 'pricePerUnit',
      headerName: 'New Unit Price',
      field: 'newPricePerUnit',
      initialWidth: 150,
      minWidth: 150,
      cellStyle: () => ({ textAlign: 'right' }),
      editable: false,
      valueFormatter: ({ value }) => {
        return NumberUtils.prettifyWithRound(value) ?? '';
      }
    },
    {
      colId: 'newBillingPeriodValue',
      field: 'newBillingPeriodValue',
      headerName: 'New Billing Period Value',
      initialWidth: 230,
      minWidth: 230,
      cellStyle: () => ({ textAlign: 'right' }),
      valueFormatter: ({ value }) => {
        return NumberUtils.prettifyWithRound(value) ?? '';
      }
    },
    {
      colId: 'newAnnualBillingPeriodValue',
      field: 'newAnnualBillingPeriodValue',
      headerName: 'New Annual Billing Value',
      initialWidth: 260,
      minWidth: 260,
      cellStyle: () => ({ textAlign: 'right' }),
      valueFormatter: ({ value }) => {
        return NumberUtils.prettifyWithRound(value) ?? '';
      }
    },
  ];
}
